import React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, ChevronRight } from "lucide-react";
import { GetStockUpOperationsSummaryResponse, StockUpSourceType } from "../../api/generated/api-client";

interface StockUpOperationStatusIndicatorProps {
  summary: GetStockUpOperationsSummaryResponse;
  sourceType: StockUpSourceType;
}

const StockUpOperationStatusIndicator: React.FC<
  StockUpOperationStatusIndicatorProps
> = ({ summary, sourceType }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Navigate to stock-up operations page with filters
    navigate(
      `/stock-up-operations?sourceType=${sourceType}&state=Pending,Submitted,Failed`
    );
  };

  // Don't render if there are no operations to show
  if ((summary.totalInQueue ?? 0) === 0 && (summary.failedCount ?? 0) === 0) {
    return null;
  }

  return (
    <div
      className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-900/40 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Zobrazit operace naskladnění s filtry"
      data-testid="stockup-status-indicator"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {(summary.totalInQueue ?? 0) > 0 && (
            <div className="flex items-center space-x-2" data-testid="queue-indicator">
              <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {summary.totalInQueue} operací ve frontě
              </span>
            </div>
          )}

          {(summary.failedCount ?? 0) > 0 && (
            <div className="flex items-center space-x-2" data-testid="failed-indicator">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-900 dark:text-red-300">
                {summary.failedCount} selhalo
              </span>
            </div>
          )}
        </div>

        <ChevronRight className="h-5 w-5 text-gray-400 dark:text-graphite-faint" />
      </div>
    </div>
  );
};

export default StockUpOperationStatusIndicator;
